import React from 'react';
import {
  makeStyles,
  Typography,
  CardActions,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@material-ui/core';

import { Line } from 'react-chartjs-2';
import { ResolutionEnumDto } from '.generated/api';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  content: {
    flex: 1,
    padding: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  count: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(4),
  },
  chart: {
    position: 'relative',
    height: '100%',
  },
  actions: {
    padding: theme.spacing(2),
    justifyContent: 'center',
  },
}));

interface CountCardProps {
  title: string;
  count?: number;
  values: number[];
  dates: Date[];
  minDate: Date;
  maxDate: Date;
  children?: React.ReactNode;
  className?: string;
  resolution?: ResolutionEnumDto;
}

const CountCard: React.FC<CountCardProps> = ({
  title,
  count,
  values,
  children,
  className,
  dates,
  minDate,
  maxDate,
  resolution,
}) => {
  const classes = useStyles();

  maxDate.setMinutes(0);
  minDate.setMinutes(0);
  maxDate.setSeconds(0);
  minDate.setSeconds(0);

  let unit: string | undefined;
  switch (resolution) {
    case ResolutionEnumDto.HOURLY:
      unit = 'hour';
      minDate.setHours(0);
      maxDate.setHours(24);
      break;
    case ResolutionEnumDto.DAILY:
      unit = 'day';
      maxDate.setHours(0);
      minDate.setHours(0);
      break;
    case ResolutionEnumDto.MONTHLY:
      unit = 'month';
      maxDate.setHours(0);
      maxDate.setDate(1);
      minDate.setHours(0);
      minDate.setDate(1);
      break;
  }

  const paddedValues = minDate < dates[0] ? [0, ...values] : [...values];
  const paddedDates = minDate < dates[0] ? [minDate, ...dates] : [...dates];
  if (maxDate > dates[dates.length - 1]) {
    paddedValues.push(0);
    paddedDates.push(maxDate);
  }

  const maxValue = Math.max(...paddedValues);
  const data = {
    labels: paddedDates,
    datasets: [
      {
        data: paddedValues,
        pointRadius: 0,
        backgroundColor: 'rgba(75,192,192,0.2)',
        borderColor: 'rgba(91, 110, 125, 1)',
        borderWidth: 3,
      },
    ],
  };

  const options = {
    animation: false as const,
    layout: {
      padding: {
        top: 20,
        bottom: 20,
      },
    },
    scales: {
      xAxes: {
        display: false,
        type: 'time' as const,
        time: {
          unit,
          unitStepSize: 1,
          min: paddedDates[0],
          max: paddedDates[paddedDates.length - 1],
        },
      },
      yAxes: {
        display: true,
        grid: { display: true, drawBorder: false },
        position: 'right' as const,
        min: 0 - maxValue * 0.03, // add padding to prevent cropped lines at the bottom
        max: maxValue * 1.03, // add padding to prevent cropped lines at the top
        ticks: {
          // only show min and max values
          callback: function (
            value: string | number,
            index: number,
            values: { value: number }[],
          ) {
            if (index === 0) {
              return '0';
            }
            if (index === values.length - 1) {
              return maxValue;
            }
            return '';
          },
        },
      },
    },
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    maintainAspectRatio: false,
  };

  return (
    <Card className={className}>
      <div className={classes.container}>
        <CardContent className={classes.content}>
          <Grid container spacing={2} style={{ height: '6rem' }}>
            <Grid item xs={6} className={classes.count}>
              <Typography align="center" variant="body1" gutterBottom>
                {title}
              </Typography>
              <Typography align="center" variant="h1">
                {count ?? '?'}
              </Typography>
            </Grid>
            {values.length > 0 && paddedValues.length > 1 && (
              <Grid item xs={6} className={classes.chart}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Line data={data} options={options as any} />
              </Grid>
            )}
          </Grid>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>{children}</CardActions>
      </div>
    </Card>
  );
};

export default CountCard;
